import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Tag } from 'lucide-react';
import PageLayout from '../components/PageLayout';
import { blogPageData } from '../data/mockData';

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = blogPageData.posts.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <PageLayout scriptLabel="Blog" heading="Post Not Found">
        <section className="py-16 px-4 bg-white">
          <div className="max-w-3xl mx-auto text-center">
            <p className="font-open-sans text-[#666] text-lg mb-8">
              Sorry, we couldn't find the blog post you're looking for.
            </p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-[#C9A84C] font-open-sans text-sm tracking-[0.15em] uppercase hover:text-[#B89740] transition-colors duration-300"
            >
              <ArrowLeft size={16} />
              Back to Blog
            </Link>
          </div>
        </section>
      </PageLayout>
    );
  }

  return (
    <PageLayout scriptLabel={post.category} heading={post.title}>
      <section className="py-16 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          {/* Back link */}
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[#C9A84C] font-open-sans text-xs tracking-[0.15em] uppercase hover:text-[#B89740] transition-colors duration-300 mb-10 block"
          >
            <ArrowLeft size={16} />
            Back to Blog
          </Link>

          {/* Post meta */}
          <div className="flex items-center gap-4 mb-8 pb-6 border-b border-gray-100">
            <span className="inline-flex items-center gap-1.5 bg-[#F3ECF8] text-[#5B2C6F] font-open-sans text-xs tracking-[0.1em] uppercase px-3 py-1.5 rounded-sm">
              <Tag size={12} />
              {post.category}
            </span>
            <span className="flex items-center gap-1.5 text-[#999] font-open-sans text-sm">
              <Calendar size={14} />
              {post.date}
            </span>
          </div>

          {/* Post content */}
          <div className="space-y-6">
            {post.content.map((paragraph, i) => (
              <p
                key={i}
                className="font-open-sans text-[#555] text-base leading-[1.9] whitespace-pre-line"
              >
                {i === 0 && (
                  <span className="font-playfair text-[#4A154B] text-xl float-left mr-1 leading-none" style={{ fontSize: '3.5rem', lineHeight: '0.8', paddingTop: '4px' }}>
                    {paragraph.charAt(0)}
                  </span>
                )}
                {i === 0 ? paragraph.slice(1) : paragraph}
              </p>
            ))}
          </div>

          {/* Bottom separator */}
          <div className="mt-12 pt-8 border-t border-gray-100">
            <div className="w-16 h-[2px] bg-[#C9A84C] mx-auto mb-8" />
            <p className="font-seaweed text-[#C9A84C] text-2xl text-center mb-4">
              Ready to begin your healing journey?
            </p>
            <div className="flex items-center justify-center gap-6">
              <Link
                to="/contact"
                className="inline-block px-8 py-3 bg-[#C9A84C] text-white font-open-sans text-xs tracking-[0.2em] uppercase hover:bg-[#B89740] transition-colors duration-300"
              >
                Contact Us
              </Link>
              <a
                href="tel:7196842180"
                className="font-playfair text-[#4A154B] text-lg font-bold hover:text-[#C9A84C] transition-colors duration-300"
              >
                719.684-2180
              </a>
            </div>
          </div>

          {/* Related Posts */}
          <div className="mt-16">
            <h3 className="font-open-sans text-[#1a1a1a] text-sm tracking-[0.2em] font-bold uppercase mb-6">
              More from the Blog
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {blogPageData.posts
                .filter((p) => p.slug !== slug)
                .slice(0, 2)
                .map((relatedPost, i) => (
                  <Link
                    key={i}
                    to={`/blog/${relatedPost.slug}`}
                    className="block bg-[#F3ECF8] rounded-sm p-6 hover:shadow-lg transition-shadow duration-300 group"
                  >
                    <span className="text-[#C9A84C] font-open-sans text-xs tracking-[0.1em] uppercase">
                      {relatedPost.category}
                    </span>
                    <h4 className="font-playfair text-[#1a1a1a] text-lg font-bold mt-2 mb-2 group-hover:text-[#5B2C6F] transition-colors duration-300">
                      {relatedPost.title}
                    </h4>
                    <p className="font-open-sans text-[#888] text-sm leading-relaxed line-clamp-2">
                      {relatedPost.excerpt}
                    </p>
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default BlogPostPage;
